import React, { useState } from 'react';
import { Modal, Radio, Space } from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import Button, { Type as ButtonType } from 'src/components/button';
import InactivationFilters from 'src/pages/reports/inactivation/filters';
import InactivationTable from 'src/pages/reports/inactivation/table';
import './style.scss';

/**
 * Inactive Profiles page component.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.data - Inactive Profiles records.
 * @param {Object} props.pagination - Pagination metadata.
 * @param {Object} props.filterOptions - Filter options for dropdowns.
 * @param {boolean} props.loading - Whether the table is loading.
 * @param {boolean} props.exporting - Whether export is in progress.
 * @param {Function} props.onFilterChange - Callback for filter changes.
 * @param {Function} props.onPaginationChange - Callback for pagination changes.
 * @param {Function} props.onExport - Callback for export action.
 * @returns {JSX.Element} The InactivationPage component.
 */
function InactivationPage({
  data,
  pagination,
  filterOptions,
  loading,
  exporting,
  onFilterChange,
  onPaginationChange,
  onSortChange,
  onExport,
}) {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('csv');

  const handleExport = () => {
    onExport(selectedFormat);
    setModalVisible(false);
  };

  return (
    <div className='reports-inactivation'>
      <div className='reports-inactivation__header'>
        <h2 className='reports-inactivation__header_counter'>
          {pagination.totalCount} {t('reports.recordsFound')}
        </h2>
        <Button
          type={ButtonType.Primary}
          className='btn-export'
          icon={exporting ? <LoadingOutlined /> : <DownloadOutlined />}
          uppercase
          disabled={exporting}
          onClick={() => setModalVisible(true)}
        >
          {exporting ? t('reports.exporting') : t('reports.exportResults')}
        </Button>
      </div>
      <InactivationFilters onApiFilter={onFilterChange} filterOptions={filterOptions} />
      <InactivationTable
        data={data}
        pagination={pagination}
        loading={loading}
        onPaginationChange={onPaginationChange}
        onSortChange={onSortChange}
      />
      <Modal
        title={t('reports.exportResultsAs')}
        visible={modalVisible}
        wrapClassName='reports-export-modal'
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key='cancel' onClick={() => setModalVisible(false)}>{t('cancel')}</Button>,
          <Button key='export' type={ButtonType.Primary} className='btn-export' onClick={handleExport}>{t('export')}</Button>,
        ]}
      >
        <Radio.Group value={selectedFormat} onChange={e => setSelectedFormat(e.target.value)}>
          <Space direction='vertical'>
            <Radio value='csv'>.CSV</Radio>
            <Radio value='xlsx'>.XLSX</Radio>
            <Radio value='pdf'>.PDF</Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
}

InactivationPage.defaultProps = {
  data: [],
  pagination: { currentPage: 1, totalCount: 0, limit: 10 },
  filterOptions: {},
  loading: false,
  exporting: false,
  onFilterChange: noop,
  onPaginationChange: noop,
  onSortChange: noop,
  onExport: noop,
};

export default InactivationPage;
